<template>
  <b-card>
    <!-- ADD MODAL -->
    <b-modal
      v-model="showAddModal"
      title="Add Other Form"
      lazy
      @hidden="resetAddForm"
    >
      <b-form @submit.prevent="submitAddOtherForm">
        <b-form-group
          label="Name"
          label-for="add-name"
        >
          <b-form-input
            id="add-name"
            v-model="addForm.name"
            required
            placeholder="Enter form name"
          />
        </b-form-group>
        <b-form-group
          label="File"
          label-for="add-file"
        >
          <b-form-file
            id="add-file"
            v-model="addForm.file"
            accept=".pdf,.doc,.docx,.xlsx,.xls,.xlsm"
            placeholder="Choose file"
          />
        </b-form-group>
      </b-form>
      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="showAddModal = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="addFormSubmitting"
          @click="submitAddOtherForm"
        >
          <b-spinner
            v-if="addFormSubmitting"
            small
            class="mr-50"
          />
          {{ addFormSubmitting ? 'Adding...' : 'Add' }}
        </b-button>
      </template>
    </b-modal>

    <b-row>
      <b-col md="12">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h4 class="mb-0">
            Other Forms
          </h4>
          <b-button
            variant="primary"
            @click="addOtherForm()"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Add Other Form
          </b-button>
        </div>
      </b-col>

      <!-- LOADING -->
      <b-col
        v-if="dataLoad"
        cols="12"
        class="d-flex align-items-center justify-content-center py-3"
      >
        <b-spinner label="Loading..." />
      </b-col>

      <!-- EMPTY -->
      <b-col
        v-else-if="!otherForms.length"
        cols="12"
      >
        <p class="text-muted text-center my-2 mb-0">
          No other forms found for this office.
        </p>
      </b-col>

      <b-col
        v-for="form in otherForms"
        :key="form.slug"
        md="6"
      >
        <b-card class="mb-2 border shadow">
          <!-- VIEW MODE -->
          <div v-if="!form.isEditing">
            <h4 class="mb-1">
              {{ form.name }}
            </h4>
            <div>
              <div class="d-flex justify-content-between">
                <div>
                  <span
                    v-if="form.isLoadingFile"
                    class="text-muted font-small-3 ml-50"
                  >
                    <b-spinner
                      variant="primary"
                      label="Spinning"
                    />
                  </span>
                  <feather-icon
                    v-else
                    icon="FileTextIcon"
                    class="cursor-pointer"
                    color="blue"
                    size="40"
                    @click="downloadOtherForm(form)"
                  />
                </div>
                <div>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="mr-1"
                    @click="form.isEditing = true"
                  >
                    <feather-icon
                      icon="EditIcon"
                      class="cursor-pointer"
                      size="14"
                    /> Edit
                  </b-button>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="deleteOtherForm(form)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      class="cursor-pointer"
                      size="14"
                    /> Delete
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- EDIT MODE -->
          <div v-else>
            <b-form-group label="Form Name">
              <b-form-input v-model="form.name" />
            </b-form-group>

            <b-form-group label="File">
              <b-form-file
                v-model="form.file"
                accept=".pdf,.doc,.docx,.xlsx,.xls,.xlsm"
              />
            </b-form-group>

            <div class="d-flex gap-1">
              <b-button
                v-if="!form.isChangeForm"
                size="sm"
                variant="success"
                class="mr-1"
                @click="editOtherForm(form)"
              >
                Save
              </b-button>
              <b-button
                v-if="form.isChangeForm"
                variant="success"
                size="sm"
                class="mr-1"
              >
                <b-spinner
                  small
                  type="grow"
                />
                Loading...
              </b-button>

              <b-button
                size="sm"
                variant="secondary"
                @click="form.isEditing = false"
              >
                Cancel
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { ref } from '@vue/composition-api'
import axios from 'axios'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  data() {
    return {
      required,
      loader: false,
      dataLoad: false,
      validationErrors: {},
      errors: {},
      showAddModal: false,
      addForm: {
        name: '',
        file: null,
      },
      addFormSubmitting: false,
    }
  },
  setup() {
    const otherForms = ref([])
    return {
      otherForms,
    }
  },

  async mounted() {
    if (this.$route.params.id) {
      this.getOtherFormsDetails()
    }
  },
  methods: {
    extractErrorMessage(error, fallback) {
      try {
        const payload = error?.response?.data?.payload
        if (!payload) return fallback
        const { message } = decryptData(payload)
        if (typeof message === 'string') return message
        if (message && typeof message === 'object') {
          const firstArrayKey = Object.keys(message).find(key => Array.isArray(message[key]))
          return firstArrayKey ? message[firstArrayKey][0] : fallback
        }
        return fallback
      } catch (e) {
        return fallback
      }
    },
    showError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'error',
        },
      })
    },
    getOtherFormsDetails() {
      this.dataLoad = true
      axios
        .get(`halal/other-forms/office/${this.$route.params.id}`)
        .then(res => {
          const { data } = decryptData(res.data.payload)
          const listKey = Object.keys(data || {}).find(key => Array.isArray(data[key]))
          const list = listKey ? data[listKey] : []
          this.otherForms = (list || []).map(t => ({
            ...t,
            isEditing: false,
            file: null,
            isLoadingFile: false,
            isChangeForm: false,
          }))
        })
        .catch(error => {
          this.showError(this.extractErrorMessage(error, 'Failed to load other forms'))
        })
        .finally(() => {
          this.dataLoad = false
        })
    },
    downloadOtherForm(form) {
      // eslint-disable-next-line no-param-reassign
      form.isLoadingFile = true

      axios.get(`/halal/other-forms/${form.slug}`)
        .then(res => {
          const downloadUrl = decryptData(res.data.payload)?.data?.data?.download_url

          if (!downloadUrl) {
            this.$toasted.show('File not found', {
              position: 'top-center',
              duration: 3000,
            })
            return null
          }

          return axios.get(downloadUrl, {
            responseType: 'blob',
          })
        })
        .then(fileRes => {
          if (!fileRes) return

          let fileName = ''
          const contentDisposition = fileRes.headers['content-disposition']

          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/)
            fileName = match ? match[1] : 'Other Form'
          }

          const blob = new Blob([fileRes.data], {
            type: fileRes.headers['content-type'],
          })

          const url = window.URL.createObjectURL(blob)

          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          link.remove()

          setTimeout(() => {
            window.URL.revokeObjectURL(url)
          }, 1000)
        })
        .catch(err => {
          if (err.response?.status === 404) {
            this.$toasted.show('File not found', {
              position: 'top-center',
              duration: 3000,
            })
          }
        })
        .finally(() => {
          // eslint-disable-next-line no-param-reassign
          form.isLoadingFile = false
        })
    },
    editOtherForm(form) {
      // eslint-disable-next-line no-param-reassign
      form.isChangeForm = true

      const formData = new FormData()
      formData.append('_method', 'PUT')
      formData.append('name', form.name)
      if (form.file) {
        formData.append('file', form.file)
      }

      axios
        .post(`halal/other-forms/${form.slug}`, formData)
        .then(() => {
          this.getOtherFormsDetails()
        })
        .catch(error => {
          this.showError(this.extractErrorMessage(error, 'Failed to update other form'))
        })
        .finally(() => {
          // eslint-disable-next-line no-param-reassign
          form.isChangeForm = false
        })
    },
    deleteOtherForm(form) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then(result => {
        if (result.isConfirmed) {
          axios
            .delete(`halal/other-forms/${form.slug}`)
            .then(() => {
              this.$swal('Deleted!', 'Deleted Successfully.', 'success')
              this.getOtherFormsDetails()
            })
            .catch(error => {
              this.showError(this.extractErrorMessage(error, 'Failed to delete other form'))
            })
        }
      })
    },
    addOtherForm() {
      this.showAddModal = true
    },
    resetAddForm() {
      this.addForm = {
        name: '',
        file: null,
      }
      this.addFormSubmitting = false
    },
    async submitAddOtherForm() {
      if (!this.addForm.name) {
        this.$swal.fire('Error', 'Please enter a name.', 'error')
        return
      }

      const officeId = this.$route.params.id
      if (!officeId) {
        this.$swal.fire('Error', 'Office ID is missing.', 'error')
        return
      }

      this.addFormSubmitting = true

      const formData = new FormData()
      formData.append('office_id', officeId)
      formData.append('name', this.addForm.name)
      if (this.addForm.file) {
        formData.append('file', this.addForm.file)
      }

      try {
        await axios.post('halal/other-forms', formData)
        this.$swal.fire('Done', 'Other form added successfully.', 'success')
        this.showAddModal = false
        this.resetAddForm()
        this.getOtherFormsDetails()
      } catch (error) {
        this.showError(this.extractErrorMessage(error, 'Failed to add other form. Please try again.'))
      } finally {
        this.addFormSubmitting = false
      }
    },
  },
}
</script>
